import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fmtAgo } from '@quadro/domain';
import { hud } from '../../components/Hud';
import { Sheet, SheetFooter } from '../../components/Sheet';
import * as api from '../../data/api';
import { closeSheet, openSheet } from '../../state/sheets';

const KEY = ['teams'] as const;
const KINDS: [api.TeamsKind, string][] = [['assigned', 'Tarefa atribuída a alguém'], ['mentioned', 'Menção num comentário'], ['comment', 'Comentário na tarefa de alguém']];
const DIGEST_STATUS: Record<string, string> = { sent: 'enviado', empty: 'nada a enviar', failed: 'falhou', sending: 'enviando' };

/**
 * Avisos no Teams (só admin): um canal recebe os avisos do Quadro e o resumo diário dos prazos,
 * por um link de Workflow do Teams. O link fica no servidor; aqui aparece só o começo dele.
 */
export function TeamsSheet() {
  const qc = useQueryClient();
  const cfg = useQuery({ queryKey: KEY, queryFn: api.fetchTeams });
  const [url, setUrl] = useState('');
  const [kinds, setKinds] = useState<api.TeamsKind[]>(['assigned', 'mentioned', 'comment']);
  const [mentionPeople, setMention] = useState(true);
  const [digest, setDigest] = useState(true);
  const [digestAt, setDigestAt] = useState('08:30');
  const [busy, setBusy] = useState<'' | 'save' | 'test' | 'clear'>('');
  const [error, setError] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const c = cfg.data;

  useEffect(() => {
    if (!c) return;
    setKinds(c.kinds); setMention(c.mentionPeople); setDigest(c.digest); setDigestAt(c.digestAt);
  }, [c]);

  async function run(what: 'save' | 'test' | 'clear', fn: () => Promise<unknown>, ok: string) {
    setBusy(what); setError(null);
    try {
      await fn();
      hud(ok);
      if (what !== 'test') { setUrl(''); await qc.invalidateQueries({ queryKey: KEY }); }
    } catch (e) { setError((e as Error).message); } finally { setBusy(''); }
  }

  const save = () => run('save', () => api.saveTeams({ url: url.trim() || undefined, kinds, mentionPeople, digest, digestAt }), c ? 'Avisos no Teams atualizados' : 'Avisos no Teams ligados');
  const test = () => run('test', () => api.testTeams(url.trim() || undefined), 'Teste enviado: confira o canal');
  const clear = () => run('clear', api.clearTeams, 'Avisos no Teams desligados');
  const toggle = (k: api.TeamsKind, on: boolean) => setKinds(l => (on ? [...l, k] : l.filter(x => x !== k)));

  return (
    <Sheet onClose={closeSheet} label="Avisos no Teams">
      <div className="sh-body tm">
        <h2>Avisos no Teams</h2>
        <p className="muted">
          Um canal da equipe recebe os avisos do Quadro, marcando a pessoa, e um resumo diário do que está atrasado, vence hoje ou é para cobrar.
        </p>

        {c ? (
          <div className={`tm-status ${c.lastError ? 'err' : 'ok'}`} role="status">
            <strong>{c.lastError ? 'Ligado, mas o último envio falhou' : 'Ligado'}</strong>
            <span className="muted">Link: <code>{c.urlHint}</code></span>
            {c.lastError && <span>{c.lastError} ({c.lastErrorAt ? fmtAgo(c.lastErrorAt) : ''})</span>}
            {!c.lastError && c.lastOkAt && <span className="muted">Último envio {fmtAgo(c.lastOkAt)}</span>}
            {c.lastDigest && <span className="muted">Resumo de {c.lastDigest.day.split('-').reverse().slice(0, 2).join('/')}: {DIGEST_STATUS[c.lastDigest.status] ?? c.lastDigest.status}</span>}
          </div>
        ) : cfg.isPending ? <p className="muted">Carregando…</p> : (
          <ol className="tm-steps">
            <li>No Teams, abra o canal, clique em <b>⋯</b> → <b>Workflows</b>.</li>
            <li>Escolha <b>“Postar em um canal quando uma solicitação de webhook for recebida”</b> e conclua.</li>
            <li>Copie o link que o Teams mostra no final e cole abaixo.</li>
          </ol>
        )}

        <div className="form">
          <label htmlFor="tmUrl">{c ? 'Trocar link' : 'Link'}</label>
          <input id="tmUrl" type="url" value={url} onChange={e => setUrl(e.target.value)} autoComplete="off" spellCheck={false}
            placeholder={c ? 'Deixe vazio para manter o atual' : 'https://…logic.azure.com/workflows/…'} />
          <label>Avisar</label>
          <div className="tm-kinds">
            {KINDS.map(([k, label]) => (
              <label key={k} className="tm-check"><input type="checkbox" checked={kinds.includes(k)} onChange={e => toggle(k, e.target.checked)} /> {label}</label>
            ))}
          </div>
          <span />
          <label className="switch"><input type="checkbox" checked={mentionPeople} onChange={e => setMention(e.target.checked)} /> Marcar a pessoa (@) no Teams</label>
          <span />
          <div className="pair">
            <label className="switch"><input type="checkbox" checked={digest} onChange={e => setDigest(e.target.checked)} /> Resumo diário dos prazos (dias úteis) às</label>
            <input type="time" aria-label="Horário do resumo" value={digestAt} disabled={!digest} onChange={e => setDigestAt(e.target.value)} style={{ width: 120, flex: 'none' }} />
          </div>
        </div>
        {error && <p className="auth-error" role="alert">{error}</p>}
      </div>
      <SheetFooter>
        {c && (confirmClear
          ? <button className="btn danger" disabled={!!busy} onClick={() => { setConfirmClear(false); void clear(); }}>Confirmar desligar</button>
          : <button className="btn" disabled={!!busy} onClick={() => setConfirmClear(true)}>Desligar</button>)}
        <span className="grow" />
        <button className="btn" onClick={() => openSheet({ kind: 'notifications' })}>Voltar</button>
        <button className="btn" disabled={!!busy || (!c && !url.trim())} onClick={test}>{busy === 'test' ? 'Enviando…' : 'Enviar teste'}</button>
        <button className="btn primary" disabled={!!busy || (!c && !url.trim()) || !/^\d{2}:\d{2}$/.test(digestAt)} onClick={save}>{busy === 'save' ? 'Salvando…' : c ? 'Salvar' : 'Ligar'}</button>
      </SheetFooter>
    </Sheet>
  );
}
